import { useTranslation } from 'react-i18next';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Badge, Button, Dialog, Input, Select } from '@easymoving/ui';
import type { Destination, ProgressStatus } from '@easymoving/types';
import { categoryOptions, roomOptions } from '../../lib/taxonomy/options';
import { DestinationSelect, StatusSelect } from './QuickSelects';
import { useCustomCategories, useCustomRooms } from '../../hooks/useUserData';
import type { ItemFilters, SortDir, SortKey } from '../../lib/query/itemFilters';

const ALL = '__all__';

export interface GalleryQuery {
  search: string;
  filters: ItemFilters;
  sortKey: SortKey;
  sortDir: SortDir;
}

export const DEFAULT_QUERY: GalleryQuery = {
  search: '',
  filters: {},
  sortKey: 'createdAt',
  sortDir: 'desc',
};

type SortPreset = 'newest' | 'oldest' | 'updated' | 'name' | 'room';
const SORT_TO_KEYDIR: Record<SortPreset, { sortKey: SortKey; sortDir: SortDir }> = {
  newest: { sortKey: 'createdAt', sortDir: 'desc' },
  oldest: { sortKey: 'createdAt', sortDir: 'asc' },
  updated: { sortKey: 'updatedAt', sortDir: 'desc' },
  name: { sortKey: 'name', sortDir: 'asc' },
  room: { sortKey: 'room', sortDir: 'asc' },
};

export function activeFilterCount(f: ItemFilters): number {
  return Object.values(f).filter((v) => v !== undefined && v !== '').length;
}

export function GalleryControls({
  query,
  onChange,
  open,
  onOpenChange,
}: {
  query: GalleryQuery;
  onChange: (q: GalleryQuery) => void;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useTranslation();
  const customRooms = useCustomRooms();
  const customCategories = useCustomCategories();

  const set = (patch: Partial<GalleryQuery>) => onChange({ ...query, ...patch });
  const setFilter = (patch: Partial<ItemFilters>) =>
    set({ filters: { ...query.filters, ...patch } });

  const count = activeFilterCount(query.filters);
  const currentSort: SortPreset =
    (Object.entries(SORT_TO_KEYDIR).find(
      ([, v]) => v.sortKey === query.sortKey && v.sortDir === query.sortDir,
    )?.[0] as SortPreset) ?? 'newest';

  return (
    <div className="flex items-center gap-2">
      <div className="relative flex-1">
        <Search className="absolute top-1/2 -translate-y-1/2 start-3 size-4 text-graphite-soft" />
        <Input
          value={query.search}
          onChange={(e) => set({ search: e.target.value })}
          placeholder={t('gallery.searchPlaceholder')}
          aria-label={t('gallery.searchPlaceholder')}
          className="ps-9"
        />
      </div>
      <Button
        variant="secondary"
        onClick={() => onOpenChange(true)}
        aria-label={t('gallery.filters')}
        className="relative shrink-0"
      >
        <SlidersHorizontal className="size-4" />
        <span className="hidden sm:inline">{t('gallery.filters')}</span>
        {count > 0 && (
          <Badge tone="petrol" className="absolute -top-2 -end-2">
            {count}
          </Badge>
        )}
      </Button>

      <Dialog
        open={open}
        onOpenChange={onOpenChange}
        title={t('gallery.filters')}
        footer={
          <div className="flex justify-between gap-2">
            <Button
              variant="ghost"
              onClick={() => set({ filters: {}, search: '' })}
              disabled={count === 0 && !query.search}
            >
              <X className="size-4" />
              {t('gallery.clearFilters')}
            </Button>
            <Button onClick={() => onOpenChange(false)}>{t('item.save')}</Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-3">
          <Labeled label={t('gallery.sort')}>
            <Select<SortPreset>
              ariaLabel={t('gallery.sort')}
              value={currentSort}
              options={[
                { value: 'newest', label: t('gallery.sortNewest') },
                { value: 'oldest', label: t('gallery.sortOldest') },
                { value: 'updated', label: t('gallery.sortUpdated') },
                { value: 'name', label: t('gallery.sortName') },
                { value: 'room', label: t('gallery.sortRoom') },
              ]}
              onValueChange={(v) => set(SORT_TO_KEYDIR[v])}
            />
          </Labeled>
          <Labeled label={t('item.room')}>
            <Select
              ariaLabel={t('item.room')}
              value={query.filters.room ?? ALL}
              options={[
                { value: ALL, label: t('gallery.allRooms') },
                ...roomOptions(t, customRooms),
              ]}
              onValueChange={(v) => setFilter({ room: v === ALL ? undefined : v })}
            />
          </Labeled>
          <Labeled label={t('item.category')}>
            <Select
              ariaLabel={t('item.category')}
              value={query.filters.category ?? ALL}
              options={[
                { value: ALL, label: t('gallery.allCategories') },
                ...categoryOptions(t, customCategories),
              ]}
              onValueChange={(v) => setFilter({ category: v === ALL ? undefined : v })}
            />
          </Labeled>
          <Labeled label={t('item.destination')}>
            <DestinationSelect
              value={query.filters.destination ?? ALL}
              lead={[{ value: ALL, label: t('gallery.allDestinations') }]}
              onValueChange={(v) =>
                setFilter({ destination: v === ALL ? undefined : (v as Destination) })
              }
            />
          </Labeled>
          <Labeled label={t('item.status')}>
            <StatusSelect
              value={query.filters.progressStatus ?? ALL}
              lead={[{ value: ALL, label: t('gallery.allStatuses') }]}
              onValueChange={(v) =>
                setFilter({ progressStatus: v === ALL ? undefined : (v as ProgressStatus) })
              }
            />
          </Labeled>
        </div>
      </Dialog>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-[13px] font-medium tracking-snug">
      <span className="text-ink">{label}</span>
      {children}
    </label>
  );
}
